<template>
  <div ref="page" class="w-full h-full absolute top-0" style="user-select: none">
    <div class="flex flex-col justify-between h-full">
      <div>
        <!-- Navbar height-->
        <div class="navbar-height w-full h-36 sm:h-18 md:h-18 flex-none hidden md:block" id="nav-placeholder"></div>
        <Header @toggle-compare-before="toggleCompareBefore" @toggle-compare-after="toggleCompareAfter" :currentPersistentStore="currentPersistentStore" />
        <ImageNagivation />
        <div id="flashes" class="relative z-20"><OldUploadsFlash /></div>
      </div>

      <template v-if="!isLoading">
        <EmptyState v-if="store.images.length === 0" />
        <Editor ref="editor" v-else @enqueueWithToken="enqueueWithToken" :currentPersistentStore="currentPersistentStore" :persistent-stores="persistentStores" />
        <Footer v-if="store.images.length > 0" />
      </template>

      <div v-if="store.isEraseRestorePanelOpen" class="md:hidden h-[142px]"></div>
      <div v-else-if="store.isAddBackgroundPanelOpen" class="md:hidden h-64"></div>
      <div v-else class="md:hidden" style="height: 78px"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, reactive, computed } from "vue";
import { v4 as uuidv4 } from "uuid";

import Client from "@/modules/internal_api/client";

import Header from "./header.vue";
import EmptyState from "./empty.vue";
import Editor from "./editor.vue";
import ImageNagivation from "./image_navigation.vue";
import Footer from "./footer.vue";

import { getNestedKey, generatePreviewFromFile, generatePreviewFromUrl, PreviewImage } from "@/modules/utils";
import { useEditorStore } from "@/stores/editor_store";

import { Image, Upload, UploadState, ProcessingState, ResultVariant } from "@/modules/internal_api/image";
import { useUploadValidation } from "@/modules/internal_api/upload_validation";

import { createPersistentStore } from "@/stores/persistent_store";
import { rbgUploadImageV101, rbgImageProcessedV100, RbgImageProcessedEventV100 } from "kaleido-event-tracker";
import OldUploadsFlash from "@/components/prism/old_uploads_flash.vue";
import emitter from "@/modules/event_bus";

const store = useEditorStore();
const { validateFile } = useUploadValidation();

const previousImagesPromise = ref<Promise<Image[]>>();
const editor = ref<InstanceType<typeof Editor>>();
const page = ref<HTMLElement>();
const isLoading = ref<boolean>(true);

onMounted(async () => {
  await loadPreviousImages();
  isLoading.value = false;
  page.value.addEventListener("mouseup", () => {
    editor.value?.handleMouseUp();
  });
  page.value.addEventListener("mousemove", (event) => {
    editor.value?.handleMouseMove(event);
  });
  page.value.addEventListener("touchend", () => {
    editor.value?.handleMouseUp();
  });

  emitter.emit("newEditorPageMounted");
  const footer = document.querySelector("footer");
  if (footer) {
    footer.remove();
  }
});

const loadPreviousImages = async () => {
  previousImagesPromise.value = Client.allImages();
  const previousImages = await previousImagesPromise.value;
  store.setImages(previousImages);
  cleanupOldImages(previousImages);
  selectLastImage();
};

const selectLastImage = () => {
  const lastSelectedImageId = localStorage.getItem("lastSelectedImageId");
  if (lastSelectedImageId) {
    const lastSelectedImage = store.images.find((image) => image.meta.id === lastSelectedImageId);
    if (lastSelectedImage) {
      store.selectImage(lastSelectedImage);
    } else {
      localStorage.removeItem("lastSelectedImageId");
    }
  }
};

const cleanupOldImages = async (images) => {
  const localStorageKeys: Array<string> = images.map((image: Image) => `image-${image.meta.id}`);

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("image-") && !localStorageKeys.includes(key)) {
      localStorage.removeItem(key);
    }
  }
};

const persistentStores = reactive(new Map<string, any>());
const currentPersistentStore = computed(() => {
  const id = store.selectedImageId;

  if (id === null) {
    return null;
  }

  if (persistentStores.has(id)) {
    return persistentStores.get(id);
  } else {
    const storeDefinition = createPersistentStore(id);
    const newStore = storeDefinition();
    newStore.persist();
    persistentStores.set(id, newStore);
    return newStore;
  }
});

const timeouts: Map<string, NodeJS.Timeout> = new Map();
const poll = async (image: Image, variant: ResultVariant): Promise<Image> => {
  const stateKey: string = `${variant}.state`;
  const nextPollKey: string = `${variant}.nextFetchIn`;
  const timeoutKey: string = `${variant}.${image.meta.id}.timeout`;

  // console.log("Start polling for image", image.meta.id, "and variant", variant);
  const executePoll = async (image, resolve, reject): Promise<void> => {
    const updatedImage = await Client.getImage(image.meta.id);
    // console.log("Received updated image", updatedImage.meta.id, "with state", getNestedKey(updatedImage, stateKey), "and next poll in", getNestedKey(updatedImage, nextPollKey), "ms");

    // The image has not finished processing yet, the server does not return
    // the preview width and height yet in this case
    // carry it over from the passed in image to prevent flickering in the editor
    if (updatedImage.meta.previewWidth === null || updatedImage.meta.previewHeight === null) {
      const { previewWidth, previewHeight } = image.meta;
      updatedImage.meta = { ...updatedImage.meta, previewWidth, previewHeight };
    }

    // The server does return a previw but we don't need to replace it on the poll,
    // makes for a smoother experience and precents flickering in the image strip
    const { preview, livePreview } = image.meta;
    updatedImage.meta = { ...updatedImage.meta, preview, livePreview };

    store.updateImage(updatedImage);
    await nextTick();

    const state = getNestedKey(updatedImage, stateKey) as ProcessingState;
    const isFinished = [ProcessingState.Finished, ProcessingState.Error].includes(state);

    if (isFinished === true) {
      const eventUploadState = updatedImage.original.state === UploadState.Finished ? "success" : "error";
      rbgUploadImageV101({ image_id: updatedImage.meta.id, editor_version: "inline_editor", upload_state: eventUploadState });

      if (state === ProcessingState.Finished) {
        let eventData: RbgImageProcessedEventV100 = {
          image_id: updatedImage.meta.id,
          foreground_type: updatedImage.meta.foregroundType,
          orientation: updatedImage.meta.orientation,
          preview_width: String(updatedImage.meta.previewWidth),
          preview_height: String(updatedImage.meta.previewHeight),
        };

        if (updatedImage.meta.fullAvailable === true) {
          eventData = {
            ...eventData,
            hd_width: String(updatedImage.meta.hdWidth),
            hd_height: String(updatedImage.meta.hdHeight),
          };
        }

        rbgImageProcessedV100(eventData);
      }

      clearTimeout(timeouts.get(timeoutKey));
      timeouts.delete(timeoutKey);
      return resolve(updatedImage);
    } else {
      const timeout = setTimeout(async () => {
        executePoll(updatedImage, resolve, reject);
      }, getNestedKey(updatedImage, nextPollKey));

      timeouts.set(timeoutKey, timeout);
    }
  };

  return new Promise((resolve, reject) => {
    executePoll(image, resolve, reject);
  });
};

const enqueue = async (upload: Upload, preview: PreviewImage) => {
  // Create a new temporary image
  const image: Image = {
    meta: {
      id: `temp-${uuidv4()}`,
      self: "/",
      preview: preview.image,
      livePreview: preview.image,
      previewHeight: preview.height,
      previewWidth: preview.width,
      ai_brush_processing_full_count: 0,
      ai_brush_iterations: null,
      ai_brush_last_iteration: null,
    },
    original: upload,
    previewResult: {
      state: ProcessingState.Processing,
      orderedAt: new Date(),
      nextFetchIn: 0,
    },
  };

  // Make sure the previous images are loaded,
  // otherwise adding the new image will mess up the order
  await previousImagesPromise.value;
  editor.value?.hideResult(image);
  await nextTick();

  store.addImage(image);
  store.selectImage(image);

  await nextTick();
  editor.value?.trustComponent?.requestToken(image);
};

const enqueueWithToken = async (image) => {
  const uploadedImage = await Client.uploadImage(image);
  store.replaceImage(image, uploadedImage);
  store.selectImage(uploadedImage);

  if (uploadedImage.original.state === UploadState.Error) {
    return;
  }

  await poll(uploadedImage, ResultVariant.Preview);

  // On slow networks this layer refresh results in a race condition with
  // the the one automatically triggered by the onAction trigger on selectImage
  // therefore we need to force the refresh here to ensure that the result will be visible
  await editor.value.refreshAllLayers(true);
  editor.value.revealResult();
};

const filePicked = async (file: File) => {
  let validationResult = validateFile(file);

  if (!validationResult.valid) {
    // TODO: Use ImageResizer
    // TODO: use validateFile again
    // Show error if this was not enough
  }

  const preview: PreviewImage = await generatePreviewFromFile(file);
  const upload: Upload = {
    state: UploadState.Pending,
    file: file,
    fileName: file.name,
    preview: preview.image,
  };

  await enqueue(upload, preview);
};

const urlPicked = async (url: string) => {
  const preview: PreviewImage = await generatePreviewFromUrl(url);
  const upload: Upload = {
    state: UploadState.Pending,
    url: url,
    preview: preview.image,
  };

  await enqueue(upload, preview);
};

type Example = { url: string; thumbnail_url: string };
const examplePicked = async (example: Example) => {
  const preview: PreviewImage = await generatePreviewFromUrl(example.url);
  const upload: Upload = {
    state: UploadState.Pending,
    url: example.url,
    preview: preview.image,
  };

  await enqueue(upload, preview);
  window.track("Images", "upload_sample_image", "Sample Image");
};

const toggleCompareBefore = () => {
  editor.value?.toggleCompareBefore();
};

const toggleCompareAfter = () => {
  editor.value?.toggleCompareAfter();
};

defineExpose({
  filePicked,
  urlPicked,
  examplePicked,
});
</script>
<style scoped>
.navbar-height {
  @media (min-width: 640px) and (max-width: 768px) {
    & {
      height: 9.2rem !important;
    }
  }
}

@media (max-width: 345px) {
  #flashes {
    padding-top: 2.5rem;
  }
}
</style>
